import React, { useEffect, useState } from "react";
import { CreditCard, Persons } from "@gravity-ui/icons";
import { Building, Users, MessageSquare, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiService from "../../api/apiService";
import timeAgo from "../../utils/getFormattedDate";
import notificationImg from "../../assets/images/notification.png";
import { getCookie } from "../../utils/cookieUtils";
import { useLocalState } from "../../contexts/LocalStateProvider";
import { loadStripe } from "@stripe/stripe-js";
import featureUpdates from "../test_arrays/featureUpdates";
import generalNotifications from "../test_arrays/generalUpdates";
import { useNotification } from "../../contexts/NotificationContext";
import { usePostHog } from "posthog-js/react";
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";

const ResponsiveNotification = ({ onClose, onUpdateCount }) => {
  const { unreadCount, notifications, setNotifications, setUnreadCount } = useNotification();
  const { updateLocalState, localState } = useLocalState();
  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  const posthog = usePostHog();

  const navigate = useNavigate();

  const unreadFeatureUpdates = unreadCount;

  useEffect(() => {
    console.log("User Notifications :", notifications);
    if (onUpdateCount) {
      onUpdateCount(unreadFeatureUpdates);
    }
  }, []);

  const markAllAsRead = async () => {
    posthog.capture("Click-MarkAllAsRead-UpdateModal", {is_user_logged_in: true})
    try {
      const updated = notifications.map((notif) => ({
        ...notif,
        is_read: true,
      }));
      setNotifications(updated);
      await apiService.markAsReadNotifications();
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      Sentry.captureException(error);
    }
  };

  const handleUpgradeMinutes = async (member) => {
    const planType = localState?.userProfileData?.subscription_type;
    const teamId = localState?.userProfileData?.team_id;

    let priceId;

    if (planType?.toLowerCase()?.includes("basic")) {
      priceId = "price_1QhT2N2MK4knwytnPL4mod13"; // Basic Plan Price ID
    } else if (planType?.toLowerCase()?.includes("business")) {
      priceId = "price_1QhT2s2MK4knwytn7JZs1e7t"; // Business Plan Price ID
    } else {
      console.error("Unknown plan type");
      return;
    }

    try {
      const response = await apiService.UpgradeMemberCheckout({
        teamUserId: member.user_id,
        teamId: teamId,
        priceId: priceId,
      });
      const sessionId = response.sessionId;

      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId });
    } catch (error) {
      console.error("Error upgrading minutes:", error);
      Sentry.captureException(error);
    }
  };

  const handleCreditRequestAction = (action, creditRequestId) => {
    fetch(
      `${process.env.REACT_APP_SERVER_ENDPOINT}/api/handle-credit-request`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
        },
        body: JSON.stringify({ action, creditRequestId }),
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          if (action === "accept") {
            // Redirect to Stripe checkout
            handleUpgradeMinutes({ user_id: data?.teamMemberId });
          } else {
            // Refresh notifications
            getNotifications();
          }
        }
      })
      .catch((error) => {
        console.error("Error handling credit request action:", error);
        Sentry.captureException(error);
      });
  };

  const handleNotificationClick = (notification) => {
    const { notification_type, metadata } = notification;

    // Ensure metadata is a valid object
    const metadataObj =
      typeof metadata === "string" ? JSON.parse(metadata) : metadata;

    if (notification_type === "Team Invite" && metadataObj?.token) {
      updateLocalState("fromNotificatioClickAccept", true);
      navigate(`/accept-invitation?token=${metadataObj?.token}`);
    }
  };

  return (
    <div className="flex flex-col h-[60vh] w-full text-white rounded-lg border border-white/10 overflow-hidden" style={{
      background: "rgba(255, 255, 255, 0.05)",
      backdropFilter: "blur(20px)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
      'backdrop-filter': 'blur(30px)',
    }}>
      {/* Header */}
      <div className="flex gap-2 p-3 pb-0 pl-4 justify-between items-center">
        <h2 className="text-xl">Updates</h2>
        <button
          onClick={markAllAsRead}
          className="text-sm text-white/70 hover:text-white bg-white/5 hover:bg-white/10 px-2.5 h-8 rounded-full transition-colors"
        >
          Mark all as read
        </button>
      </div>

      {/* Simplified Tab Bar - Only shows "Feature updates" as a title/indicator */}
      <div className="flex px-4 gap-4 border-b border-white/5 h-12 items-center" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
         {/* Removed "General" button */}
         {/* Keep Feature updates text, styled as a non-button element or simple button */}
         <span className="h-full flex items-center text-sm border-b-2 border-white"> {/* Keep the active style */}
           Feature updates{" "}
           {unreadCount > 0 && (
             <span className="inline-flex items-center justify-center min-w-4 px-1 h-4 ml-1 bg-gradient-to-br from-[#FC10F2] to-[#3078E4] text-white rounded-full text-[10px]">
               {unreadCount}
             </span>
           )}
         </span>
         {/* Removed the second button */}
      </div>

      {/* Content area - Always show Feature updates */}
      <div
        className="flex flex-col w-full overflow-y-auto"
        style={{
          height: "calc(60vh - 96px)",
          scrollbarWidth: "none",
          msOverflowStyle: "none"
        }}
      >
        {/* Hide scrollbar in Chrome/Safari */}
        <style jsx>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>

        {/* --- Always render FEATURE UPDATES --- */}
        <div className="flex flex-col w-full h-full">
          {notifications.map((notification, index) => (
            <div
              key={notification.user_notification_id}
              className="flex items-start gap-3 p-4 border-b border-white/5 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center justify-center min-w-[36px] h-9 rounded-full bg-[#3e3e43] mt-1">
                {/* Use img directly since it's always Feature Updates */}
                <img src={notification.svg_url} className="w-4 h-4 object-contain" alt="Update icon"/>
              </div>

              <div className="flex flex-col gap-0 w-full">
                <p className="text-sm">{notification.title}</p>
                <p className="text-sm font-extralight text-[#C5C3C6]">
                  {notification.description}
                </p>
              </div>
            </div>
          ))}
          {/* Add a message if there are no notifications */}
           {notifications.length === 0 && (
              <div className="p-4 text-center text-sm text-white/60">
                No new updates right now.
              </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default ResponsiveNotification;